import React, { useState, useEffect } from 'react';
import { User, Lock, Mail, Phone, UserPlus, MapPin, Map, Home, RefreshCw, Shield } from 'lucide-react';
import AuthFormContainer from './AuthFormContainer';

// Generate simple CAPTCHA
const generateCaptcha = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export default function RegistrationPage({ onRegister, setPage }) {
  const [formData, setFormData] = useState({
    name: '', mobile: '', email: '', password: '', gst: '',
    address: '', city: '', state: '', pincode: ''
  });
  const [error, setError] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCaptcha(generateCaptcha());
  }, []);

  const regenerateCaptcha = () => setCaptcha(generateCaptcha());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, mobile, email, password, address, city, state, pincode, gst } = formData;

    // Required field check
    if (!name || !mobile || !email || !password || !address || !city || !state || !pincode) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!/^[0-9]{10}$/.test(mobile)) {
      setError('Mobile number must be exactly 10 digits.');
      return;
    }

    if (!/^[0-9]{6}$/.test(pincode)) {
      setError('Pincode must be exactly 6 digits.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Invalid email format.');
      return;
    }

    // Password validation: min 5 chars, at least 1 letter and 1 number
if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{5,}$/.test(password)) {
  setError('Password must be at least 5 characters long and include both letters and numbers.');
  return;
}

    if (captchaInput.trim().toLowerCase() !== captcha.toLowerCase()) {
      setError('Captcha did not match. Try again.');
      regenerateCaptcha();
      return;
    }

    const payload = {
      Vid: 0,
      VCode: mobile,
      Vname: name,
      Address1: address,
      City: city,
      State: state,
      Pincode: pincode,
      GST: gst,
      VType: 'Customer',
      Phone: mobile,
      Mail: email,
      Mobile: mobile,
      Active: 0,
      ComId: password,
      Sp_SqlAction: 'Insert'
    };

   try {
  setLoading(true);
  const response = await fetch('https://posservice.revampapps.com/DMLVendorOrimShort', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
 if (!response.ok) throw new Error('Failed to register.');

  const data = await response.json();

  if (data?.DMLVendorOrim?.Table?.[0]?.Status === 'Error') {
    const message = data.DMLVendorOrim.Table[0].Message;
  if (message === 'Duplicate Number') {
  setError('Number already exists.');
  return;
} else if (message === 'Duplicate Email') {
  setError('Email already exists.');
  return;
}
  }
onRegister(formData); // Pass email and OTP-related info
setPage('verify-email');

} catch (err) {
  console.error(err);
  setError('Registration failed. Try again later.');
} finally {
  setLoading(false);
}
  };

  return (
    <AuthFormContainer
      title="Create your Account"
      footer={
        <p>Already have an account?{' '}
          <button onClick={() => setPage('login')} className="font-medium text-teal-600 hover:text-teal-500">
            Sign In
          </button>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        {error && <p className="text-sm text-red-600 text-center bg-red-100 p-3 rounded-lg">{error}</p>}

        <Input icon={<User />} name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" />
        <Input icon={<Mail />} name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" />
        <Input icon={<Phone />} name="mobile" value={formData.mobile} onChange={handleChange} placeholder="Mobile Number" maxLength={10} />
        <Input icon={<Lock />} name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Password" />
        <Input icon={<UserPlus />} name="gst" value={formData.gst} onChange={handleChange} placeholder="GST Number (Optional)" />
        <Input icon={<Home />} name="address" value={formData.address} onChange={handleChange} placeholder="Address" />
        <Input icon={<MapPin />} name="city" value={formData.city} onChange={handleChange} placeholder="City" />
        <Input icon={<Map />} name="state" value={formData.state} onChange={handleChange} placeholder="State" />
        <Input icon={<MapPin />} name="pincode" value={formData.pincode} onChange={handleChange} placeholder="Pincode" maxLength={6} />

        {/* CAPTCHA */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Captcha</label>
          <div className="flex items-center gap-3">
            <div className="bg-gray-200 px-4 py-2 rounded-lg text-lg font-bold tracking-wider">
              {captcha}
            </div>
            <button type="button" onClick={regenerateCaptcha} className="text-gray-500 hover:text-teal-600">
              <RefreshCw size={18} />
            </button>
          </div>
          <div className="mt-2">
            <Input icon={<Shield />} name="captchaInput" value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value)} placeholder="Enter Captcha" />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg text-white font-semibold ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'
            } transition-colors shadow-md hover:shadow-lg`}
          >
            {loading ? 'Registering...' : 'Register'}
          </button>
        </div>
      </form>
    </AuthFormContainer>
  );
}

const Input = ({ icon, name, value, onChange, placeholder, type = 'text', maxLength }) => (
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
      {React.cloneElement(icon, { className: 'h-5 w-5 text-gray-400' })}
    </div>
    <input
      name={name}
      value={value}
      onChange={onChange}
      type={type}
      maxLength={maxLength}
      required
      className="block w-full px-3 py-3 pl-12 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm transition-shadow duration-300 shadow-sm"
      placeholder={placeholder}
    />
  </div>
);
